import {isoToLocalDate} from "../stores/StoreUtilities";
import INetWorth from "./INetWorth";
import {compareAsc, isToday} from "date-fns";
import IModelItem from "./IModelItem";
import Schedule from "./Schedule";
import {SnapshotType} from "../API";
import IBudget from "./iBudget";

export type SnapshotDetailTypename = "Asset" | "Liability" | "Income" | "Deduction" | "Tax" | "Expense" | ""

export interface ISnapshotDetail {
  id: string
  typename: string
  balance: number
  amount?: number
  schedule?: Schedule
}

export class SnapshotDetail implements ISnapshotDetail {
  id: string
  typename: string
  balance: number
  schedule?: Schedule
  amount?: number

  constructor (data: ISnapshotDetail) {
    this.id = data.id
    this.typename = data.typename
    this.balance = data.balance ?? 0
    this.amount = data.amount
    if (data.schedule) {
      this.schedule = new Schedule(data.schedule)
    }
  }
}

class Snapshot implements IModelItem {
  id: string
  createdAt: string
  updatedAt: string
  accountId: string
  userId: string
  modelId: string
  date: Date
  description: string
  snapshotType: SnapshotType
  archived: boolean
  details: ISnapshotDetail[]

  constructor (data: any) {
    this.id = data.id
    this.createdAt = data.createdAt
    this.updatedAt = data.updatedAt
    this.accountId = data.accountId
    this.userId = data.userId
    this.modelId = data.modelId
    this.date = isoToLocalDate(data.date)
    this.description = data.description
    this.snapshotType = data.snapshotType ?? SnapshotType.NetWorth
    this.archived = data.archived === true
    this.details = []
    if (data.details) {
      this.loadSnapshotDetails(data.details)
    }
  }

  getTypename(): string {
    return "Snapshot"
  }

  loadSnapshotDetails(data: any) {
    if (typeof data === "string") {
      const details = JSON.parse(data)
      if (details.length) {
        this.details = details.map((item: ISnapshotDetail) => new SnapshotDetail(item))
      }
    } else {
      this.details = data
    }
  }

  getSnapshotDetail(item: INetWorth | IBudget, filterByStartDate: boolean = false): ISnapshotDetail | undefined {
    let detail: ISnapshotDetail | undefined
    const typename = item.getTypename()
    if (!filterByStartDate) {
      detail = this.details.find((detail: ISnapshotDetail) => detail.id === item.id && detail.typename === typename)
    } else {
      // Only return snapshot details that are on or after the item start date
      const startDate = item.startDate
      if (startDate && compareAsc(startDate, this.date) <= 0) {
        detail = this.details.find((detail: ISnapshotDetail) => detail.id === item.id && detail.typename === typename)
      }
    }

    return detail
  }

  setSnapshotDetail(detail: ISnapshotDetail) {
    const index = this.details.findIndex((d: ISnapshotDetail) => d.id === detail.id && d.typename === detail.typename)
    if (index >= 0) {
      this.details[index] = detail
    } else {
      this.details.push(detail)
    }
  }

  getSnapshotBalance(id: string, typename: string) {
    const detail = this.details.find((detail: ISnapshotDetail) => detail.id === id && detail.typename === typename)
    return detail ? detail.balance : 0
  }

  getSnapshotAmount(id: string, typename: string) {
    const detail = this.details.find((detail: ISnapshotDetail) => detail.id === id && detail.typename === typename)
    return detail ? detail.amount ?? 0 : 0
  }
  get isCurrent(): boolean {
    return (this.id === "current" || isToday(this.date))
  }

}

export default Snapshot